import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import logo from "@/assets/logo_final.png";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/dashboard";

  useEffect(() => {
    if (!loading && user) {
      navigate(nextPath, { replace: true });
    }
  }, [loading, user, navigate, nextPath]);

  // Prevent DOM reconciliation errors (insertBefore) by not rendering
  // the form while auth is loading or user is already set (navigating away)
  if (loading || user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Preencha todos os campos");
      return;
    }
    setSubmitting(true);
    const result = await login(email, password);
    setSubmitting(false);
    if (result.error) {
      setError(result.error);
    } else {
      navigate(nextPath);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 py-8 md:p-4">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-6 md:mb-8">
          <img src={logo} alt="Nutrir Corpo & Mente" className="w-36 h-36 md:w-32 md:h-32 mx-auto mb-2 object-contain" />
        </div>

        <div className="bg-card rounded-2xl p-6 shadow-card border border-border">
          <h2 className="text-2xl md:text-xl font-semibold text-foreground mb-6 text-center">Entrar</h2>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-base md:text-sm">Email</Label>
              <Input id="email" type="email" placeholder="seu@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-[52px] text-base md:h-10 md:text-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-base md:text-sm">Senha</Label>
              <div className="relative">
                <Input id="password" type={showPass ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="h-[52px] text-base md:h-10 md:text-sm" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPass(!showPass)}>
                  {showPass ? <EyeOff className="w-5 h-5 md:w-4 md:h-4" /> : <Eye className="w-5 h-5 md:w-4 md:h-4" />}
                </button>
              </div>
            </div>
            <p className={`text-sm text-destructive ${error ? "block" : "hidden"}`}>{error}</p>
            <Button type="submit" disabled={submitting} className="w-full h-[52px] md:h-10 text-base md:text-sm font-semibold gradient-primary text-primary-foreground hover:opacity-90 transition-opacity">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
              Entrar
            </Button>
          </form>
          <div className="mt-5 text-center text-base md:text-sm text-muted-foreground">
            <Link to="/forgot-password" className="text-primary hover:underline">Esqueceu a senha?</Link>
          </div>
          <div className="mt-3 text-center text-base md:text-sm text-muted-foreground">
            Não tem conta?{" "}
            <Link to={searchParams.get("next") ? `/register?next=${encodeURIComponent(searchParams.get("next")!)}` : "/register"} className="text-primary font-medium hover:underline">Cadastre-se</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
